<template>
  <div class="report-page">
    <!-- Sales Report Filtering Option -->
    <div class="dropdown-container">
      <!-- Select FSM Name -->
      <div class="dropdown-item">
        <label for="fsmSelect">FSM</label>
        <select id="fsmSelect" v-model="selectedFsm" @change="loadAsmList">
          <option value="">SELECT FSM</option>
          <option v-for="fsm in fsmList" :key="fsm.profile_id" :value="fsm.profile_id">
            {{ fsm.name }}
          </option>
        </select>
      </div>

      <!-- Select ASM Name -->
      <div class="dropdown-item">
        <label for="asmSelect">ASM</label>
        <select id="asmSelect" v-model="selectedAsm" @change="loadDistributorList">
          <option value="">SELECT ASM</option>
          <option v-for="asm in asmList" :key="asm.profile_id" :value="asm.profile_id">
            {{ asm.name }}
          </option>
        </select>
      </div>

      <!-- Select Distributor's Name -->
      <div class="dropdown-item">
        <label for="distributorSelect">Distributor</label>
        <select id="distributorSelect" v-model="selectedDistributor" @change="loadDealerList">
          <option value="">SELECT Distributors</option>
          <option v-for="distributor in distributorList" :key="distributor.profile_id" :value="distributor.profile_id">
            {{ distributor.name }}
          </option>
        </select>
      </div>

      <!-- Select dealer_visit's Name -->
      <div class="dropdown-item">
        <label for="dealerSelect">Dealer</label>
        <select id="dealerSelect" v-model="selectedDealer">
          <option value="">SELECT Dealers</option>
          <option v-for="dealer in dealerList" :key="dealer.profile_id" :value="dealer.profile_id">
            {{ dealer.name }}
          </option>
        </select>
      </div>

      <!-- Enter Sales Record Number -->
      <div class="dropdown-item">
        <label for="orderNo">Reference No</label>
        <input
            type="text"
            v-model="searchQuery"
            placeholder="Search..."
            class="search-input"
            @keydown.enter="performSearch"
        />
      </div>

      <!-- Pick sales report starting date -->
      <div class="dropdown-item">
        <label for="startDatePicker">Start Date</label>
        <input
            type="date"
            v-model="startDate"
            class="search-input"
            @change="handleDateChange"
            :max="currentDate"
        />
      </div>

      <!-- Pick sales report ending date -->
      <div class="dropdown-item">
        <label for="endDatePicker">End Date</label>
        <input
            type="date"
            v-model="endDate"
            class="search-input"
            @change="handleDateChange"
            :max="currentDate"
        />
      </div>

      <!-- Search -->
      <div class="search-container single">
        <button type="button" class="btn button-default" :disabled="loading" @click="performSearch">
          <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
          {{ loading ? "Searching..." : "Search" }}
        </button>
      </div>

      <!-- Print Floating Button -->
      <button @click="printPdf" class="print-button">
        <i class="fa-solid fa-print"></i>
        <span class="tooltip-text">Print</span>
      </button>

      <!-- Download Floating Button -->
      <button @click="checkEmptyValidations()" class="download-button">
        <i class="fa-solid fa-download"></i>
        <span class="tooltip-text">Download</span>
      </button>

      <!-- Dialog Box to select document type to download -->
      <div v-if="showDialog" class="dialog-overlay">
        <div class="dialog-box">
          <h4>Select Document Type</h4>
          <div class="download-container">
            <div class="download-item">
              <button @click="exportToExcel()" class="download-excel">
                <img src="https://img.icons8.com/color/96/microsoft-excel-2019--v1.png" width="24" alt="Excel">
              </button>
              <span>Excel</span>
            </div>

            <div class="download-item">
              <button @click="exportToPdf" class="download-pdf">
                <img src="https://img.icons8.com/external-fauzidea-flat-fauzidea/64/external-pdf-file-file-extension-fauzidea-flat-fauzidea.png" width="24" alt="PDF">
              </button>
              <span>PDF</span>
            </div>
          </div>

          <button @click="showDialog = false" class="close-button">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Show total of Sale, Refill, Empty quantities-->
    <div v-if="orders.length > 0" class="tiles-container">
      <div class="tile tile-sales">
        <div class="tile-header">
          <h5>Sale</h5>
          <i class="fa fa-chart-line corner-icon text-primary"></i>
        </div>
        <span class="tile-span text-primary">{{ Math.floor(finalTotal?.grand_total_sale_qty || 0).toLocaleString() }}</span>
      </div>
      <div class="tile tile-refill">
        <div class="tile-header">
          <h5>Refill</h5>
          <i class="fa fa-sync-alt corner-icon text-success"></i>
        </div>
        <span class="tile-span text-success">{{ Math.floor(finalTotal?.grand_total_refil_qty || 0).toLocaleString() }}</span>
      </div>
      <div class="tile tile-empty">
        <div class="tile-header">
          <h5>Empty</h5>
          <i class="fa fa-box-open corner-icon text-danger"></i>
        </div>
        <span class="tile-span text-danger">{{ Math.floor(finalTotal?.grand_total_empty_qty || 0).toLocaleString() }}</span>
      </div>
    </div>

    <!-- Show loading indicator -->
    <div v-if="loading" class="loading-spinner">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- Show sales report as a tabular format -->
    <div v-if="orders.length > 0" class="table-container">
      <div class="card">
        <div class="table-responsive">
          <table class="table">
            <thead>
            <tr>
              <th>Increment Id</th>
              <th>Created At</th>
              <th>Dealer</th>
              <th>Sale Qty</th>
              <th>Refill Qty</th>
              <th>Empty Qty</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="order in orders" :key="order.id">
              <td>{{ order.increment_id}}</td>
              <td>{{ formatDate(order.created_at) }}</td>
              <td>{{ order.business_name }}</td>
              <td>{{ Math.floor(order.total_sale_qty).toLocaleString() }}</td>
              <td>{{ Math.floor(order.total_refil_qty).toLocaleString() }}</td>
              <td>{{ Math.floor(order.total_empty_qty).toLocaleString() }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Show message when data not found for given criteria -->
    <div v-if="!loading && orders.length === 0" class="not-found-container">
      <img src="../../../assets/vectors/data_not_found.png" alt="No Invoices" class="not-found-image"
      />
      <p class="not-found-message">Results not found!</p>
    </div>
  </div>
</template>

<script>
import {
  fetchFsmList,
  fetchAsmList,
  fetchDistributorList,
  fetchDealerListManagement,
  fetchDailySaleReport
} from '@/services/dataService';
import {useToast} from "vue-toastification";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default {
  data() {
    const currentDate = new Date().toISOString().split('T')[0];
    return {
      token: localStorage.getItem('token_set') || '',
      fsmList: [],
      asmList: [],
      distributorList: [],
      dealerList: [],
      selectedFsm: '',
      selectedAsm: '',
      selectedDistributor: '',
      selectedDealer: '',
      searchQuery: '',
      startDate: currentDate,
      endDate: currentDate,
      orders: [],
      finalTotal: {
        grand_total_sale_qty: 0,
        grand_total_empty_qty: 0,
        grand_total_refil_qty: 0,
        grand_total_qty: 0
      },
      loading: false,
      showModal: false,
      modalTitle: '',
      currentDate,
      modalIdentifierLabel: '',
      modalIdentifierValue: '',
      modalMessageLabel: '',
      modalMessageValue: '',
      modalStatusLabel: '',
      modalStatusValue: '',
      modalDateTimeLabel: '',
      modalDateTimeValue: '',
      modalComplaintId: null,
      showDialog: false
    };
  },

  watch: {
    showDialog(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  },

  created() {
    this.loadFsmList();
    this.performSearch();
  },

  methods: {
    //Date Range Validation
    handleDateChange() {
      if (this.startDate && this.endDate) {
        const start = new Date(this.startDate);
        const end = new Date(this.endDate);

        if (end < start) {
          this.showErrorToast('Invalid date range picked.');
          this.endDate = ''; // Clear the invalid end date
        }
      }
    },

    //Show Error Message in Toaster
    showErrorToast(message) {
      const toast = useToast(); // Initialize toaster
      toast.error(message, {
        position: "top-right",
        timeout: 3000,
      });
    },

    //Fetch FSM List
    async loadFsmList() {
      const profileId = localStorage.getItem('profile_id');
      if (!profileId) {
        console.error('Profile ID not found in localStorage');
        return;
      }
      try {
        const response = await fetchFsmList(profileId, this.token);
        this.fsmList = response.data.data;
      } catch (error) {
        console.error('Error fetching FSM list:', error);
      }
    },

    //Fetch ASM List
    async loadAsmList() {
      if (this.selectedFsm) {
        try {
          const response = await fetchAsmList(localStorage.getItem('profile_id'), this.selectedFsm, this.token);
          this.asmList = response.data.data;
        } catch (error) {
          console.error('Error fetching ASM list:', error);
        }
      } else {
        this.asmList = [];
      }
    },

    //Fetch Distributor List
    async loadDistributorList() {
      if (this.selectedAsm) {
        try {
          const response = await fetchDistributorList(localStorage.getItem('profile_id'), this.selectedAsm, this.token);
          this.distributorList = response.data.data;
        } catch (error) {
          console.error('Error fetching Distributor list:', error);
        }
      } else {
        this.distributorList = [];
      }
    },

    //Fetch Dealers List API Handle for selected distributor
    async loadDealerListManagement() {
      if (this.selectedDistributor) {
        try {
          const response = await fetchDealerListManagement(localStorage.getItem('profile_id'), this.selectedDistributor, this.token);
          this.dealerList = response.data.data;
        } catch (error) {
          console.error('Error fetching dealer_visit list:', error);
        }
      } else {
        this.dealerList = [];
      }
    },

    //Fetch dealer_visit List
    async loadDealerList() {
      if (this.selectedDistributor) {
        await this.loadDealerListManagement();
      } else {
        this.dealerList = [];
      }
    },

    //Filter Sales Report According to the Given Date Range, FSM, ASM, Distributor, dealer_visit and Reference No
    async performSearch() {
      this.orders = [];
      this.finalTotal = {};
      this.loading = true;
      try {
        const formattedStartDate = this.startDate ? this.formatDateForApi(this.startDate) : null;
        const formattedEndDate = this.endDate ? this.formatDateForApi(this.endDate) : null;
        const searchData = {
          fsm_id: this.selectedFsm || null,
          asm_id: this.selectedAsm || null,
          distributor_id: this.selectedDistributor || null,
          dealer_id: this.selectedDealer || null,
          order_no: this.searchQuery || null,
          start_date: formattedStartDate,
          end_date: formattedEndDate
        };
        console.log('Search Data:', searchData);
        const response = await fetchDailySaleReport(searchData, this.token);
        console.log('API Response:', response.data);
        this.orders = response.data.data.orders;
        this.finalTotal = response.data.data.grand_total || {
          grand_total_sale_qty: 0,
          grand_total_empty_qty: 0,
          grand_total_refil_qty: 0,
          grand_total_qty: 0
        };
      } catch (error) {
        console.error('Error fetching daily sale report:', error);
      } finally {
        this.loading = false;
      }
    },

    //Format DateTime
    formatDateForApi(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      return `${year}-${month}-${day}`;
    },

    //Format DateTime
    formatDate(dateString) {
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-');
      const [time, period] = timePart.split(/(am|pm)/i);
      const [hours, minutes] = time.split(':');

      const date = new Date(year, month - 1, day, period.toLowerCase() === 'pm' ? parseInt(hours) + 12 : hours, minutes);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },

    //Check empty validations of the filtered data
    checkEmptyValidations() {
      if (!Array.isArray(this.orders) || this.orders.length === 0) {
        this.showErrorToast('No data available to export.');
      } else {
        this.showDialog = true;
      }
    },

    //Download the sales report as an Excel file
    exportToExcel() {
      // Get current date and user info
      const now = new Date();
      const reportGeneratedBy = localStorage.getItem('full_name') || 'Unknown User';
      const reportGeneratedAt = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')} ${now.toLocaleTimeString()}`;

      // Function to find name by profile_id or return fallback
      const findNameById = (list, id) => {
        if (!id) return "N/A"; // Not selected
        const foundItem = list.find(item => item.profile_id === id);
        return foundItem ? foundItem.name : "Unknown"; // Return name or "Unknown" if not found
      };

      // Fetch names based on selected IDs
      const selectedFsmName = findNameById(this.fsmList, this.selectedFsm);
      const selectedAsmName = findNameById(this.asmList, this.selectedAsm);
      const selectedDistributorName = findNameById(this.distributorList, this.selectedDistributor);
      const selectedDealerName = findNameById(this.dealerList, this.selectedDealer);

      // Report criteria details
      const generatedByData = [
        ["Report Generated By", reportGeneratedBy],
        ["Report Generated At", reportGeneratedAt],
        [],
        ["Criteria for Report Generation"],
        ["Start Date", this.startDate.replace(/-/g, '.')],
        ["End Date", this.endDate.replace(/-/g, '.')],
        ["Field Sales Manager", selectedFsmName],
        ["Area Sales Manager", selectedAsmName],
        ["Distributor", selectedDistributorName],
        ["dealer_visit", selectedDealerName],
        ["Reference No", this.searchQuery || "N/A"],
      ];

      // Prepare the main report data
      const worksheetData = [
        ["Increment Id", "Created At", "dealer_visit", "Sale Qty", "Refill Qty", "Empty Qty"]
      ];

      this.orders.forEach(order => {
        worksheetData.push([
          order.increment_id,
          this.formatDate(order.created_at),
          order.business_name,
          Math.floor(order.total_sale_qty).toLocaleString(),
          Math.floor(order.total_refil_qty).toLocaleString(),
          Math.floor(order.total_empty_qty).toLocaleString()
        ]);
      });

      // Get totals from finalTotal
      const totalSaleQty = this.finalTotal.grand_total_sale_qty;
      const totalRefilQty = this.finalTotal.grand_total_refil_qty;
      const totalEmptyQty = this.finalTotal.grand_total_empty_qty;

      // Prepare the new "Sale Report Summary" data
      // Prepare the new "Sale Report Summary" data with headers
      const summaryData = [
        ["Total Sale Quantity", Math.floor(totalSaleQty).toLocaleString()],
        ["Total Refill Quantity", Math.floor(totalRefilQty).toLocaleString()],
        ["Total Empty Quantity", Math.floor(totalEmptyQty).toLocaleString()],
      ];

      // Create workbook and worksheets
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const generatedBySheet = XLSX.utils.aoa_to_sheet(generatedByData);
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

      // Set column widths for readability
      worksheet['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 30 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
      generatedBySheet['!cols'] = [{ wch: 25 }, { wch: 50 }];
      summarySheet['!cols'] = [{ wch: 25 }, { wch: 20 }]; // Adjust width for the summary sheet

      // Append sheets in the correct order (Sales Report first, Sale Report Summary second, Generated By third)
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Report');
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Sale Report Summary');
      XLSX.utils.book_append_sheet(workbook, generatedBySheet, 'Generated By');

      // Generate file name
      const formattedStartDate = this.startDate.replace(/-/g, '.');
      const formattedEndDate = this.endDate.replace(/-/g, '.');
      const filename = `Dealer Sales Report ${formattedStartDate} to ${formattedEndDate}.xlsx`;

      // Save the Excel file
      XLSX.writeFile(workbook, filename);

      // Hide the file type selection dialog box after download the file
      this.showDialog = false;
    },

    //Download the sales report as an PDF file
    exportToPdf() {
      // Configuration of the document size and orientation
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Configure page layout
      const marginLeft = 15;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const footerHeight = 20;
      const tableBottomMargin = 10;

      // Get the report generated username and date time
      const now = new Date();
      const reportGeneratedBy = (localStorage.getItem('full_name') || 'Unknown User')
          .toLowerCase()
          .replace(/\b\w/g, char => char.toUpperCase());
      const reportGeneratedAt = now.toLocaleString();

      // Report Title
      doc.setFontSize(16);
      doc.text("dealer_visit Sales Report", pageWidth / 2, 15, { align: "center" });

      // Extra space between title and report details
      let yPosition = 30; // Adjusted for better spacing

      // Function to find name by profile_id or return fallback
      const findNameById = (list, id) => {
        if (!id) return "N/A";
        const foundItem = list.find(item => item.profile_id === id);
        return foundItem ? foundItem.name : "Unknown";
      };

      // Fetch names based on selected IDs
      const selectedFsmName = findNameById(this.fsmList, this.selectedFsm);
      const selectedAsmName = findNameById(this.asmList, this.selectedAsm);
      const selectedDistributorName = findNameById(this.distributorList, this.selectedDistributor);
      const selectedDealerName = findNameById(this.dealerList, this.selectedDealer);
      const referenceNo = this.searchQuery || "N/A";

      // Generate details as a list
      const details = [
        [`Start Date: ${this.startDate.replace(/-/g, '.')}`, `End Date: ${this.endDate.replace(/-/g, '.')}`],
        [`Field Sales Manager: ${selectedFsmName}`, `Area Sales Manager: ${selectedAsmName}`],
        [`Distributor: ${selectedDistributorName}`, `Dealer: ${selectedDealerName}`],
        [`Reference No: ${referenceNo}`, ``]
      ];

      // Show details of the filtering
      doc.setFontSize(10);
      details.forEach(row => {
        doc.text(row[0], marginLeft, yPosition);
        doc.text(row[1], pageWidth / 2 + 10, yPosition);
        yPosition += 8; // Increased line spacing
      });

      // Table Headers
      const columns = [
        { header: "Increment ID", dataKey: "increment_id" },
        { header: "Created At", dataKey: "created_at" },
        { header: "dealer_visit", dataKey: "business_name" },
        { header: "Sale Qty", dataKey: "total_sale_qty" },
        { header: "Refill Qty", dataKey: "total_refil_qty" },
        { header: "Empty Qty", dataKey: "total_empty_qty" }
      ];

      // Table Data
      const rows = this.orders.map(order => ({
        increment_id: order.increment_id,
        created_at: this.formatDate(order.created_at),
        business_name: order.business_name,
        total_sale_qty: Math.floor(order.total_sale_qty).toLocaleString(),
        total_refil_qty: Math.floor(order.total_refil_qty).toLocaleString(),
        total_empty_qty: Math.floor(order.total_empty_qty).toLocaleString(),
      }));

      // Generate Table
      autoTable(doc, {
        startY: yPosition,
        head: [columns.map(col => col.header)],
        body: rows.map(row => Object.values(row)),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2, textColor: [0, 0, 0], fillColor: [255, 255, 255] },
        headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
        margin: { bottom: footerHeight + tableBottomMargin },
        didDrawPage: function (data) {
          const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
          doc.setFontSize(10);

          // Footer Background
          doc.setFillColor(240, 240, 240);
          doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");

          // Footer: Page Number (Centered)
          doc.text(`${currentPage}`, pageWidth / 2, pageHeight - 10, { align: "center" });

          // Footer: Report Generated Info
          doc.setTextColor(105, 105, 105); // Dark Grey
          doc.text("Report Generated By", marginLeft, pageHeight - 15);

          doc.setTextColor(0, 0, 0); // Black
          doc.text(reportGeneratedBy, marginLeft, pageHeight - 10);

          doc.setTextColor(105, 105, 105); // Dark Grey
          doc.text("Report Generated At", pageWidth - 60, pageHeight - 15);

          doc.setTextColor(0, 0, 0); // Black
          doc.text(reportGeneratedAt, pageWidth - 60, pageHeight - 10);
        }
      });

      // Calculate available space on the current page
      let lastTableY = doc.lastAutoTable.finalY || 0;
      let remainingSpace = pageHeight - lastTableY - footerHeight - 20; // Leave space for footer

      const summaryTableHeight = 30; // Approximate height for the summary table

      // If there is not enough space, add a new page
      if (remainingSpace < summaryTableHeight) {
        doc.addPage();
        lastTableY = 20; // Reset y position for new page
      }

      // Adding the "Summary" title before the totals table
      const summaryTitleY = lastTableY + 10; // Adjusted positioning

      doc.setFontSize(12);
      doc.text("Summary of the Report", marginLeft, summaryTitleY);

      // Position for the summary table
      const summaryTableY = summaryTitleY + 5;

      // Summary table data
      const totalsData = [
        { label: "Total Sale Quantity", value: Math.floor(this.finalTotal.grand_total_sale_qty).toLocaleString() },
        { label: "Total Refill Quantity", value: Math.floor(this.finalTotal.grand_total_refil_qty).toLocaleString() },
        { label: "Total Empty Quantity", value: Math.floor(this.finalTotal.grand_total_empty_qty).toLocaleString() }
      ];

      autoTable(doc, {
        startY: summaryTableY,
        body: totalsData.map(item => [item.label, item.value]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2, textColor: [0, 0, 0], fillColor: [255, 255, 255] },
        headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
        columnStyles: {
          0: { fillColor: [180, 180, 180], textColor: [0, 0, 0], align: 'left' },
          1: { align: 'left' }
        },
        margin: { bottom: footerHeight + tableBottomMargin },
        didDrawPage: function (data) {
          const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
          doc.setFontSize(10);

          // Footer Background
          doc.setFillColor(240, 240, 240);
          doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");

          // Footer: Page Number (Centered)
          doc.text(`${currentPage}`, pageWidth / 2, pageHeight - 10, { align: "center" });

          // Footer: Report Generated Info
          doc.setTextColor(105, 105, 105);
          doc.text("Report Generated By", marginLeft, pageHeight - 15);
          doc.setTextColor(0, 0, 0);
          doc.text(reportGeneratedBy, marginLeft, pageHeight - 10);

          doc.setTextColor(105, 105, 105);
          doc.text("Report Generated At", pageWidth - 60, pageHeight - 15);
          doc.setTextColor(0, 0, 0);
          doc.text(reportGeneratedAt, pageWidth - 60, pageHeight - 10);
        }
      });

      // Save PDF
      const filename = `Dealer_Sales_Report_${this.startDate.replace(/-/g, '.')}_to_${this.endDate.replace(/-/g, '.')}.pdf`;
      doc.save(filename);

      // Open PDF in a new browser tab (without download)
      // const pdfBlob = doc.output('blob'); // Get PDF as a Blob
      // const pdfUrl = URL.createObjectURL(pdfBlob); // Create an object URL
      // window.open(pdfUrl); // Open the PDF in a new tab

      // Hide the file type selection dialog box after download the file
      this.showDialog = false;
    },

    //Print the generated sales report pdf file
    printPdf() {
      // Check filtered data empty validations
      if (!Array.isArray(this.orders) || this.orders.length === 0) {
        this.showErrorToast('No data available to print.');
        return;
      }

      // Configuration of the document size and orientation
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Configure page layout
      const marginLeft = 15;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const footerHeight = 20;
      const tableBottomMargin = 10;

      // Get the report generated username and date time
      const now = new Date();
      const reportGeneratedBy = (localStorage.getItem('full_name') || 'Unknown User')
          .toLowerCase()
          .replace(/\b\w/g, char => char.toUpperCase());
      const reportGeneratedAt = now.toLocaleString();

      // Report Title
      doc.setFontSize(16);
      doc.text("dealer_visit Sales Report", pageWidth / 2, 15, { align: "center" });

      // Extra space between title and report details
      let yPosition = 30; // Adjusted for better spacing

      // Function to find name by profile_id or return fallback
      const findNameById = (list, id) => {
        if (!id) return "N/A";
        const foundItem = list.find(item => item.profile_id === id);
        return foundItem ? foundItem.name : "Unknown";
      };

      // Fetch names based on selected IDs
      const selectedFsmName = findNameById(this.fsmList, this.selectedFsm);
      const selectedAsmName = findNameById(this.asmList, this.selectedAsm);
      const selectedDistributorName = findNameById(this.distributorList, this.selectedDistributor);
      const selectedDealerName = findNameById(this.dealerList, this.selectedDealer);
      const referenceNo = this.searchQuery || "N/A";

      // Generate details as a list
      const details = [
        [`Start Date: ${this.startDate.replace(/-/g, '.')}`, `End Date: ${this.endDate.replace(/-/g, '.')}`],
        [`Field Sales Manager: ${selectedFsmName}`, `Area Sales Manager: ${selectedAsmName}`],
        [`Distributor: ${selectedDistributorName}`, `Dealer: ${selectedDealerName}`],
        [`Reference No: ${referenceNo}`, ``]
      ];

      // Show details of the filtering
      doc.setFontSize(10);
      details.forEach(row => {
        doc.text(row[0], marginLeft, yPosition);
        doc.text(row[1], pageWidth / 2 + 10, yPosition);
        yPosition += 8; // Increased line spacing
      });

      // Table Headers
      const columns = [
        { header: "Increment ID", dataKey: "increment_id" },
        { header: "Created At", dataKey: "created_at" },
        { header: "dealer_visit", dataKey: "business_name" },
        { header: "Sale Qty", dataKey: "total_sale_qty" },
        { header: "Refill Qty", dataKey: "total_refil_qty" },
        { header: "Empty Qty", dataKey: "total_empty_qty" }
      ];

      // Table Data
      const rows = this.orders.map(order => ({
        increment_id: order.increment_id,
        created_at: this.formatDate(order.created_at),
        business_name: order.business_name,
        total_sale_qty: Math.floor(order.total_sale_qty).toLocaleString(),
        total_refil_qty: Math.floor(order.total_refil_qty).toLocaleString(),
        total_empty_qty: Math.floor(order.total_empty_qty).toLocaleString(),
      }));

      // Generate Table
      autoTable(doc, {
        startY: yPosition,
        head: [columns.map(col => col.header)],
        body: rows.map(row => Object.values(row)),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2, textColor: [0, 0, 0], fillColor: [255, 255, 255] },
        headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
        margin: { bottom: footerHeight + tableBottomMargin },
        didDrawPage: function (data) {
          const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
          doc.setFontSize(10);

          // Footer Background
          doc.setFillColor(240, 240, 240);
          doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");

          // Footer: Page Number (Centered)
          doc.text(`${currentPage}`, pageWidth / 2, pageHeight - 10, { align: "center" });

          // Footer: Report Generated Info
          doc.setTextColor(105, 105, 105); // Dark Grey
          doc.text("Report Generated By", marginLeft, pageHeight - 15);

          doc.setTextColor(0, 0, 0); // Black
          doc.text(reportGeneratedBy, marginLeft, pageHeight - 10);

          doc.setTextColor(105, 105, 105); // Dark Grey
          doc.text("Report Generated At", pageWidth - 60, pageHeight - 15);

          doc.setTextColor(0, 0, 0); // Black
          doc.text(reportGeneratedAt, pageWidth - 60, pageHeight - 10);
        }
      });

      // Calculate available space on the current page
      let lastTableY = doc.lastAutoTable.finalY || 0;
      let remainingSpace = pageHeight - lastTableY - footerHeight - 20; // Leave space for footer

      const summaryTableHeight = 30; // Approximate height for the summary table

      // If there is not enough space, add a new page
      if (remainingSpace < summaryTableHeight) {
        doc.addPage();
        lastTableY = 20; // Reset y position for new page
      }

      // Adding the "Summary" title before the totals table
      const summaryTitleY = lastTableY + 10; // Adjusted positioning

      doc.setFontSize(12);
      doc.text("Summary of the Report", marginLeft, summaryTitleY);

      // Position for the summary table
      const summaryTableY = summaryTitleY + 5;

      // Summary table data
      const totalsData = [
        { label: "Total Sale Quantity", value: Math.floor(this.finalTotal.grand_total_sale_qty).toLocaleString() },
        { label: "Total Refill Quantity", value: Math.floor(this.finalTotal.grand_total_refil_qty).toLocaleString() },
        { label: "Total Empty Quantity", value: Math.floor(this.finalTotal.grand_total_empty_qty).toLocaleString() }
      ];

      autoTable(doc, {
        startY: summaryTableY,
        body: totalsData.map(item => [item.label, item.value]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2, textColor: [0, 0, 0], fillColor: [255, 255, 255] },
        headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
        columnStyles: {
          0: { fillColor: [180, 180, 180], textColor: [0, 0, 0], align: 'left' },
          1: { align: 'left' }
        },
        margin: { bottom: footerHeight + tableBottomMargin },
        didDrawPage: function (data) {
          const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
          doc.setFontSize(10);

          // Footer Background
          doc.setFillColor(240, 240, 240);
          doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");

          // Footer: Page Number (Centered)
          doc.text(`${currentPage}`, pageWidth / 2, pageHeight - 10, { align: "center" });

          // Footer: Report Generated Info
          doc.setTextColor(105, 105, 105);
          doc.text("Report Generated By", marginLeft, pageHeight - 15);
          doc.setTextColor(0, 0, 0);
          doc.text(reportGeneratedBy, marginLeft, pageHeight - 10);

          doc.setTextColor(105, 105, 105);
          doc.text("Report Generated At", pageWidth - 60, pageHeight - 15);
          doc.setTextColor(0, 0, 0);
          doc.text(reportGeneratedAt, pageWidth - 60, pageHeight - 10);
        }
      });

      // Show pdf in the in-built pdf reader in the web browser
      const pdfBlob = doc.output('blob'); // Get PDF as a Blob
      const pdfUrl = URL.createObjectURL(pdfBlob); // Create an object URL

      // Create an invisible iframe
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0px';
      iframe.style.height = '0px';
      iframe.style.border = 'none';

      // Append the iframe to the body
      document.body.appendChild(iframe);

      iframe.src = pdfUrl;
      iframe.onload = function () {
        iframe.contentWindow.focus();
        iframe.contentWindow.print(); // Trigger print dialog
      };
    },
  }
};
</script>

<style scoped>
/* Ensure the body and html take the full height of the page */
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0; /* Background color for the page */
}

/* Container for the entire section */
.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* Center items vertically */
  height: 100%; /* Take full height of the page */
  width: 100%; /* Take full width of the page */
  box-sizing: border-box; /* Ensure padding doesn't affect width */
}

/* Loading spinner styles */
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff; /* Preserved color for spinner */
}

/* Container for dropdowns and search box */
.dropdown-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr); /* 4 columns for each row */
  gap: 15px; /* Adjust spacing between items */
  width: 100%; /* Ensure container takes full width */
  justify-content: center; /* Center dropdowns horizontally */
  margin-bottom: 20px; /* Space between dropdowns and table */
}

/* Each dropdown item with label */
.dropdown-item {
  flex: 1;
  min-width: 180px; /* Minimum width for each dropdown item */
  max-width: 250px; /* Maximum width for each dropdown item */
}

/* Styling for labels */
label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px; /* Adjust font size for better readability */
}

/* Styling for select elements */
select {
  width: 100%; /* Ensure select takes full width of its container */
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px; /* Adjust font size for better readability */
  transition: border-color 0.3s, box-shadow 0.3s;
}

input[type="text"] {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
}

input[type="date"] {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
}

/* Focus styling for select elements */
select:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

/* Styling for search input */
.search-input {
  flex: 1;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px; /* Adjust font size for better readability */
  transition: border-color 0.3s, box-shadow 0.3s;
}

/* Focus styling for search input */
.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none; /* Remove the default outline */
}

/* Styling for search button */
.search-container {
  display: flex;
  align-items: center;
  justify-content: left;
}

.btn.button-default {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #ffffff;
  font-size: 14px; /* Adjust font size for better readability */
  cursor: pointer;
  transition: background-color 0.3s, transform 0.3s;
}

.btn.button-default:hover {
  background-color: #0056b3;
  transform: scale(1.05);
}

.btn.button-default:active {
  transform: scale(0.95);
}

.tiles-container {
  display: flex;
  justify-content: space-around;
  width: 100%;
  margin-top: 20px;
}

.tile {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  flex: 1;
  margin: 0 10px;
  max-width: 200px; /* Reduce the width of the tiles */
  position: relative;
  transition: transform 0.3s, box-shadow 0.3s;
}

.tile:hover {
  transform: translateY(-5px);
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
}

.tile-header {
  display: flex;
  justify-content: center; /* Center the content horizontally */
  align-items: center; /* Center the content vertically */
  flex-direction: column; /* Stack the title and icon vertically */
}

.corner-icon {
  font-size: 24px;
  color: #007bff;
  margin-top: 5px; /* Add some space between the title and the icon */
}

.tile h5 {
  margin: 0;
  font-size: 18px;
  color: #333;
  text-align: center; /* Center the text */
}

.tile-span {
  display: block;
  margin-top: 10px;
  font-size: 24px;
  font-weight: bold;
  color: #007bff;
}

.tile-sales {
  background-color: #e0f7fa; /* Light cyan */
}

.tile-refill {
  background-color: #e8f5e9; /* Light green */
}

.tile-empty {
  background-color: #fff3e0; /* Light orange */
}

/* Table styling */
.table-container {
  width: 100%;
  margin-top: 20px;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th, .table td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.table th {
  background-color: #000;
  color: #fff;
  font-weight: bold;
}

.table td {
  background-color: #ffffff;
}

/* Results not found image */
.not-found-image {
  display: block;
  max-width: 250px;
  width: 100%;
  height: auto;
  margin: 0 auto;
}

/* Results not found message */
.not-found-message {
  font-size: 25px;
  font-weight: bold;
  color: #ccc;
  margin-top: -25px;
  text-align: center;
}

/* Print Floating Buttons Styling */
.print-button {
  position: fixed;
  bottom: 80px;
  right: 20px;
  background-color: #db9100; /* Adjust the color as needed */
  width: 50px;
  height: 50px;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 50%; /* Makes it look rounded */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Adds a shadow for better visibility */
  cursor: pointer;
  z-index: 1000; /* Ensures it stays above other content */
  transition: background-color 0.3s ease;
}

/* Download Floating Buttons Styling */
.download-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #007bff; /* Adjust the color as needed */
  width: 50px;
  height: 50px;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 50%; /* Makes it look rounded */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Adds a shadow for better visibility */
  cursor: pointer;
  z-index: 1000; /* Ensures it stays above other content */
  transition: background-color 0.3s ease;
}

/* Floating Buttons Icon */
.print-button i,
.download-button i{
  font-size: 20px;
}

/* Tooltip Styling */
.tooltip-text {
  visibility: hidden;
  background-color: #333;
  color: #fff;
  text-align: center;
  padding: 6px 10px;
  border-radius: 4px;
  position: absolute;
  bottom: 60px; /* Distance above the button */
  right: 50%;
  transform: translateX(50%);
  white-space: nowrap;
  font-size: 12px;
  z-index: 1001;
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* Show tooltip on hover */
.print-button:hover .tooltip-text,
.download-button:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

/* Dialog Styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Dialog Box Styles */
.dialog-box {
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

/* Center the download buttons in a row */
.download-container {
  display: flex;
  justify-content: center;
  gap: 20px; /* Space between buttons */
}

/* Align text below buttons */
.download-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Make the close button the same width as the dialog */
.close-button {
  width: 100%;
  margin-top: 15px;
  background: black;
  color: white;
  padding: 10px 0;
  border: none;
  cursor: pointer;
  font-size: 16px;
  border-radius: 5px;
}

/* Excel sheet download button */
.download-excel {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  border: 2px solid #1D6F42;
  background-color: rgba(29, 111, 66, 0.3);
  margin: 20px 20px 5px 20px;
}

/* Pdf download button */
.download-pdf {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  border: 2px solid #c01015;
  background-color: rgba(192, 16, 21, 0.3);
  margin: 20px 20px 5px 20px;
}

/* Excel sheet download button, Pdf download button button icon*/
.download-excel img, .download-pdf img {
  width: 45px;
}
</style>